import { useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { apiGet, apiPost } from "@/lib/api";

export const Route = createFileRoute("/_authenticated/admin/app-update")({
  beforeLoad: ({ context }) => {
    const user = (context as { user?: { role?: string | null } }).user;
    if (user?.role !== "admin") throw redirect({ to: "/admin" });
  },
  component: AppUpdatePage,
});

interface NoticeRow {
  id: string;
  message: string;
  createdAt: string;
  createdByName: string;
}

const DEFAULT_MESSAGE = "We've released an important update. Please refresh the app to get the latest version.";

function formatWhen(iso: string): string {
  const d = new Date(iso.includes("T") ? iso : `${iso.replace(" ", "T")}Z`);
  return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}

function AppUpdatePage() {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "app-notices"],
    queryFn: () => apiGet<{ data: NoticeRow[] }>("/api/app-notice"),
  });
  const notices = data?.data ?? [];

  const send = useMutation({
    mutationFn: () => apiPost("/api/app-notice", { message: message.trim() || undefined }),
    onSuccess: () => {
      toast.success("Update notice sent to everyone using the app right now");
      setMessage("");
      queryClient.invalidateQueries({ queryKey: ["admin", "app-notices"] });
    },
    onError: (err: Error) => toast.error(err.message || "Could not send the notice"),
  });

  function confirmAndSend() {
    if (
      window.confirm(
        "Show a one-time \"please refresh\" notice to everyone who has the app open right now?\n\nOnly send this AFTER the new version has finished deploying."
      )
    ) {
      send.mutate();
    }
  }

  return (
    <div className="max-w-2xl space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-text-primary">App Update Notice</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          After an important update that affects live stations or team-wide apps, tell everyone who has the app open to refresh.
        </p>
      </div>

      <div className="rounded-xl border border-border bg-card p-4 space-y-3">
        <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-1">
          <li>It appears once, only for people who already have the app open. Anyone who opens it after the update already has the new version.</li>
          <li>It goes away when they press close, or by itself after 30 seconds. They can also tap "Refresh now".</li>
          <li>It is in the app only: no email, SMS or phone notification.</li>
          <li>Send it only after the deployment has finished and is green.</li>
        </ul>

        <div>
          <label htmlFor="notice-message" className="text-xs font-medium text-muted-foreground">
            Message (optional)
          </label>
          <textarea
            id="notice-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={2}
            maxLength={200}
            placeholder={DEFAULT_MESSAGE}
            className="mt-1 w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          />
        </div>

        <Button onClick={confirmAndSend} disabled={send.isPending}>
          <Send className="h-4 w-4 mr-1.5" />
          {send.isPending ? "Sending..." : "Notify active users to refresh"}
        </Button>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-semibold">Recent notices</p>
        {isLoading && <Skeleton className="h-14 w-full rounded-xl" />}
        {!isLoading && notices.length === 0 && (
          <div className="rounded-xl border border-border bg-card py-10 text-center">
            <BellRing className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No update notices sent yet.</p>
          </div>
        )}
        {notices.map((n) => (
          <div key={n.id} className="rounded-xl border border-border bg-card px-3 py-2.5">
            <p className="text-xs text-muted-foreground">
              {n.createdByName} · {formatWhen(n.createdAt)}
            </p>
            <p className="text-sm mt-0.5">{n.message}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
